import { css, SimpleInterpolation } from 'styled-components';

const sizes = {
  desktopLarge: '1200px',
  desktop: '1024px',
  tablet: '768px',
  phoneLarge: '600px',
  phone: '480px',
  phoneSmall: '360px',
} as const;

type Size = keyof typeof sizes;
type Bound = 'max' | 'min' | 'lessThan' | 'greaterThan';
type Orientation = 'portrait' | 'landscape';

type MediaFunction = (
  strings: TemplateStringsArray,
  ...interpolations: SimpleInterpolation[]
) => ReturnType<typeof css>;

type MediaHelpers = {
  [B in Bound]: {
    [S in Size]: (orientation?: Orientation) => MediaFunction;
  };
};

const getPixelValue = (str: string): number => {
  return Number(str.replace('px', ''));
};

const getMediaFunction = (mode: Bound) => {
  return Object.fromEntries(
    Object.entries(sizes).map(([key, value]) => {
      let query: string;
      if (mode === 'max') {
        query = `(max-width: ${value})`;
      } else if (mode === 'min') {
        query = `(min-width: ${value})`;
      } else if (mode === 'lessThan') {
        const num = getPixelValue(value) - 0.02;
        query = `(max-width: ${num}px)`;
      } else if (mode === 'greaterThan') {
        const num = getPixelValue(value) + 0.02;
        query = `(min-width: ${num}px)`;
      } else {
        throw new Error('Invalid mode');
      }
      return [
        key,
        (orientation?: Orientation) =>
          (strings: TemplateStringsArray, ...interpolations: SimpleInterpolation[]) => {
            const orientationStr = orientation ? ` and (orientation: ${orientation})` : '';
            return css`
              @media (${query}${orientationStr}) {
                ${css(strings, ...interpolations)}
              }
            `;
          },
      ];
    })
  ) as MediaHelpers[Bound];
};

const media: MediaHelpers = {
  max: getMediaFunction('max'),
  min: getMediaFunction('min'),
  lessThan: getMediaFunction('lessThan'),
  greaterThan: getMediaFunction('greaterThan'),
};

export default media;
